// "Something is loading" for the shell: the header's colour stripe runs while
// any GraphQL request or route change is in flight (owner request
// 2026-10-17). Every GraphQL client's fetch is wrapped in `trackFetch`
// (boot/urql.ts, core/publicGraphqlClient.ts) and the router brackets each
// navigation (router/index.ts) — pages never call this themselves.
//
// The stripe waits SHOW_AFTER_MS before it starts, so the many quick
// requests (a poll, a cached refetch) never make the header flicker, and
// once running it stays for at least MIN_VISIBLE_MS so a load that ends just
// after it started does not blink.
import { readonly, ref } from 'vue'

export const SHOW_AFTER_MS = 200
export const MIN_VISIBLE_MS = 400

const active = ref(false)
/** True while the shell should show that something is loading. */
export const busy = readonly(active)

let count = 0
let shownAt = 0
let showTimer: ReturnType<typeof setTimeout> | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null

function clear(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer) clearTimeout(timer)
  return null
}

/** Marks one piece of work as started; call the returned function when it
 *  ends (extra calls are ignored, so a `finally` and an error path may both
 *  call it). */
export function beginBusy(): () => void {
  count += 1
  if (count === 1) {
    hideTimer = clear(hideTimer)
    if (!active.value && !showTimer) {
      showTimer = setTimeout(() => {
        showTimer = null
        active.value = true
        shownAt = Date.now()
      }, SHOW_AFTER_MS)
    }
  }
  let ended = false
  return () => {
    if (ended) return
    ended = true
    count -= 1
    if (count > 0) return
    showTimer = clear(showTimer)
    if (!active.value) return
    const left = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt))
    hideTimer = setTimeout(() => {
      hideTimer = null
      if (count === 0) active.value = false
    }, left)
  }
}

/** A fetch that counts as busy from the request until its response (or
 *  failure) arrives. */
export function trackFetch(inner: typeof fetch): typeof fetch {
  return (input, init) => {
    const end = beginBusy()
    return inner(input, init).finally(end)
  }
}

/** Test seam: back to idle, timers dropped. */
export function resetBusy(): void {
  count = 0
  showTimer = clear(showTimer)
  hideTimer = clear(hideTimer)
  active.value = false
}
